import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import './ClientList.css';
import ClientListDetail from './ClientListDetail';
import { API_URL } from '../../utils/auth';
import Auth from '../../utils/auth';
import Pagination from '@mui/material/Pagination';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { getDecodedParam } from '../../utils/urlUtils';

const formatNumberWithTwoDecimals = (number) => {
    return parseFloat(number).toFixed(2);
};

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

const getStatus = (score) => {
    if (score >= 0 && score <= 3.99) return { color: 'at-risk', label: 'At-Risk' };
    if (score >= 4.0 && score <= 6.99) return { color: 'fair', label: 'Fair' };
    return { color: 'good', label: 'Good' };
};

const formatProgress = (value) => {
    const num = parseFloat (value);
     if (isNaN(num)) return '0';
    return Math.round(num).toString();
};

const ClientList = ({ onClientClick, selectedStatus, searchQuery, sortConfig, selectedClients, onCheckboxChange, onSelectAll, onClientsLoad }) => {
    const location = useLocation();
    const query = useQuery();
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const [currentPage, setCurrentPage] = useState(() => {
        const saved = searchParams.get('page');
        return saved ? parseInt(saved) : 1;
    });
    const [totalPages, setTotalPages] = useState(1);
    const [clientsPerPage, setClientsPerPage] = useState(() => {
        const saved = sessionStorage.getItem('clientsPerPage');
        const fromUrl = searchParams.get('pageSize');
        if (fromUrl) return parseInt(fromUrl);
        if (saved) return parseInt(saved);
        return 10;
    });
    
    const searchParamsObj = Object.fromEntries(searchParams);
    const previousSearchQuery = useRef(searchQuery);
    const onboardingStep = searchParamsObj.onboardingStep || '';
    const lastContactedRange = query.get('lastContactedRange') || '';
    const icp = getDecodedParam(searchParams, 'icp');
    const billingStatus = getDecodedParam(searchParams, 'billingStatus');
    const company = getDecodedParam(searchParams, 'company');
    const program = getDecodedParam(searchParams, 'program');
    const userIdFromParams = searchParams.get('userId');
    const workspaceId = Auth.getWorkspaceId();
    const userId = Auth.getUser()?.id;

    const fetchClients = async () => {
        try {
            setLoading(true);
            
            // Use page 1 if search query is different from previous
            const pageToUse = searchQuery !== previousSearchQuery.current ? 1 : currentPage;
            
            const response = await axios.get(`${API_URL}/clients/clients`, {
                params: {
                    workspaceId,
                    userId: userIdFromParams || undefined,
                    page: pageToUse,
                    pageSize: clientsPerPage,
                    search: searchQuery || undefined,
                    status: selectedStatus || undefined,
                    sortField: sortConfig?.field || undefined,
                    sortDirection: sortConfig?.direction || undefined,
                    onboardingStep: onboardingStep || undefined,
                    lastContactedRange: lastContactedRange || undefined,
                    icp: icp || undefined,
                    billingStatus: billingStatus || undefined,
                    company: company || undefined,
                    program: program || undefined,
                }
            });
    
            const clientsData = response.data.clients;
    
            const clientsWithExtendedData = clientsData.map(client => ({
                ...client,
                formattedCRS: formatNumberWithTwoDecimals(client.clientRetentionScore),
                companyName: client.companyName || "Unknown",
                onboardingProgress: formatProgress(client.onboardingProgress),
                lastContacted: client.lastContacted || null,
            }));
    
            setClients(clientsWithExtendedData);
            if (onClientsLoad) {
                onClientsLoad(clientsWithExtendedData);
            }
            setTotalPages(response.data.totalPages);
            setLoading(false);
        } catch (err) {
            console.error('Error fetching clients:', err);
            setError('Error fetching clients');
            setLoading(false);
        }
    };

    useEffect(() => {
        if (workspaceId) { 
            fetchClients();
        }
    }, [workspaceId, currentPage, searchQuery, selectedStatus, sortConfig, onboardingStep, lastContactedRange, icp, billingStatus, clientsPerPage, userIdFromParams, program]);

    useEffect(() => {
        const pageFromParams = parseInt(searchParams.get('page')) || 1;
        setCurrentPage(pageFromParams);
    }, [searchParams]);

    // Reset to page 1 when search query changes
    useEffect(() => {
        if (searchQuery !== previousSearchQuery.current && searchQuery !== undefined) {
            previousSearchQuery.current = searchQuery;
            // Always reset to page 1 immediately when search query changes
            setCurrentPage(1);
            setSearchParams(prevParams => {
                const newParams = new URLSearchParams(prevParams);
                newParams.set('page', '1');
                return newParams;
            });
        }
    }, [searchQuery, setSearchParams]);

    const handlePageChange = (event, page) => {
        setCurrentPage(page);
        setSearchParams(prevParams => {
            const newParams = new URLSearchParams(prevParams);
            newParams.set('page', page.toString());
            return newParams;
        });
    };

    const handleClientsPerPageChange = (event) => {
        const newValue = parseInt(event.target.value);
        setClientsPerPage(newValue);
        sessionStorage.setItem('clientsPerPage', newValue);
        setCurrentPage(1);
        setSearchParams(prevParams => {
            const newParams = new URLSearchParams(prevParams);
            newParams.set('page', '1');
            newParams.set('pageSize', newValue.toString());
            return newParams;
        });
    };

    useEffect(() => {
        const saved = sessionStorage.getItem('clientsPerPage');
        if (saved) {
            setClientsPerPage(parseInt(saved));
        }
    }, []);

    const handleClientUpdate = (updatedClient) => {
        setClients(prevClients => 
            prevClients.map(client => 
                client.id === updatedClient.id ? updatedClient : client
            )
        );
    };

    const clearSavedPage = () => {
        localStorage.removeItem('clientsCurrentPage');
        setCurrentPage(1);
    };

    const getSelectedClientsData = () => {
        return clients.filter(client => selectedClients.has(client.id));
    };

    return (
        <div className="client-list">
            <div className="client-list-header">
                <span>
                    <input
                        type="checkbox"
                        checked={clients.length > 0 && selectedClients.size === clients.length}
                        ref={input => {
                            if (input) input.indeterminate = (selectedClients.size > 0 && selectedClients.size < clients.length);
                        }}
                        onChange={(e) => {
                            if (e.target.checked) {
                                onSelectAll(clients);
                            } else {
                                onSelectAll([]);
                            }
                        }}
                        style={{ width: '16px', height: '16px', accentColor: '#572AF1' }}
                    />
                </span>
                <span>Client</span>
                <span>Company</span>
                <span>CRS</span>
                <span></span>
                <span>Last Contacted</span>
                <span>Programs</span>
                <span>Onboarding</span>
            </div>
            <div className="client-list-body">
                {loading ? (
                    <div>Loading clients...</div>
                ) : error ? (
                    <div>{error}</div>
                ) : clients.length > 0 ? (
                    clients.map((client) => (
                        <ClientListDetail
                            key={client.id}
                            client={{
                                ...client,
                                formattedCRS: formatNumberWithTwoDecimals(client.clientRetentionScore),
                                onboardingProgress: client.onboardingProgress,
                                lastContacted: client.lastContacted,
                                companyName: client.companyName,
                                crsHistory: client.crsHistory,
                            }}
                            onClick={() => { onClientClick(client); }}
                            showCheckbox={true}
                            checked={selectedClients.has(client.id)}
                            onCheckboxChange={(e) => onCheckboxChange(client.id, e.target.checked)}
                        />
                    ))
                ) : (
                    <div>No clients match your criteria.</div>
                )}
            </div>

            <div className="pagination-controls">
                <div className="clients-per-page-selector">
                    <label htmlFor="clientsPerPage">Clients per page:</label>
                    <select
                        id="clientsPerPage"
                        value={clientsPerPage}
                        onChange={handleClientsPerPageChange}
                    >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                    </select>
                </div>

                {totalPages > 1 && (
                    <div className="pagination-container">
                        <Pagination
                            count={totalPages}
                            page={currentPage}
                            onChange={handlePageChange}
                            sx={{
                                '& .MuiPaginationItem-root': {
                                    color: '#291B3B',
                                    fontSize: '14px',
                                    '&:hover': {
                                        backgroundColor: 'rgba(87, 42, 241, 0.1)',
                                    },
                                },
                                '& .MuiPaginationItem-page.Mui-selected': {
                                    backgroundColor: 'rgba(87, 42, 241, 1)',
                                    color: 'white',
                                    '&:hover': {
                                        backgroundColor: 'rgba(87, 42, 241, 0.8)',
                                    },
                                },
                                '& .MuiPaginationItem-ellipsis': {
                                    color: '#291B3B',
                                },
                            }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default ClientList;